import {
  Box,
  Typography,
  Stack,
  Stepper as MuiStepper,
  Step,
  StepLabel,
  StepIconProps,
  StackProps,
  styled,
  StepConnector,
  stepConnectorClasses,
} from "@mui/material";
import Check from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";

export const Stepper = ({
  stepIndex = 0,
  completed,
  sx,
  ...stackProps
}: StackProps & { stepIndex?: number; completed?: boolean }) => {
  const { t } = useTranslation();

  const steps = [
    t("stepper.selectExam"),
    t("stepper.termsAndConditions"),
    t("stepper.candidateDetails"),
    t("stepper.confirm"),
  ];

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      sx={{
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        gap: 4,
        ...sx,
      }}
      {...stackProps}
    >
      {!completed && (
        <Box sx={{ minWidth: "50%" }}>
          <Typography variant="h3">{steps[stepIndex]}</Typography>
          <Typography variant="body1" color="textSecondary">
            {t("stepper.step")} <strong>{stepIndex + 1}</strong>{" "}
            {t("stepper.of")} {steps.length}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: { xs: "100%", md: "auto" },
          overflow: "hidden",
        }}
      >
        <MuiStepper
          activeStep={!completed ? stepIndex : steps.length}
          alternativeLabel
          connector={<Connector />}
          sx={
            completed
              ? { width: { xs: "100%", md: "50%" }, margin: "0 auto" }
              : { mx: `calc((-100% / (${steps.length} * 2)) + 10px)` }
          }
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel slots={{ stepIcon: StepIcon }} />
            </Step>
          ))}
        </MuiStepper>
      </Box>
    </Stack>
  );
};

const StepIcon = ({ active, completed, className, icon }: StepIconProps) => (
  <StepIconRoot ownerState={{ completed, active }} className={className}>
    {completed ? <Check /> : String(icon)}
  </StepIconRoot>
);

const StepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme }) => ({
  backgroundColor: "#fff",
  border: `2px solid ${theme.palette.grey[500]}`,
  zIndex: 1,
  color: theme.palette.text.secondary,
  width: 32,
  height: 32,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  variants: [
    {
      props: ({ ownerState }) => ownerState.active,
      style: {
        backgroundColor: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
        color: "#fff",
      },
    },
    {
      props: ({ ownerState }) => ownerState.completed,
      style: {
        backgroundColor: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        color: "#fff",
      },
    },
  ],
}));

const Connector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 15,
    left: "-50%",
    right: "50%",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.grey[500],
  },
}));
